// src/pages/batismo/Batismo.js

import React, { useEffect, useState } from 'react';
import { getTurmasBatismo, createInscricaoBatismo } from '../../services/api';
import { calcIdade, maskPhone, formatDate, formatFileSize } from '../../utils/helpers';
import FileUpload from '../../components/forms/FileUpload';
import Toast from '../../components/common/Toast';
import { uploadArquivo, uploadMultiplos } from '../../services/uploadHelper';
import batismoIcon from '../../assets/batismo_icon.png';

const STATUS = { OPEN: 'open', CLOSED: 'closed' };

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  let s = String(dateStr).trim();
  if (s.includes('T')) s = s.split('T')[0];
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [d, m, y] = s.split('/');
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-');
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  return null;
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

// Abre: dia seguinte à reuniao1 da turma anterior
// Fecha: 1 dia antes da reuniao1 da turma atual
const calcStatus = (turmas) => {
  if (!turmas || turmas.length === 0) return { status: STATUS.CLOSED, turma: null };
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const sorted = [...turmas].sort((a, b) => {
    const da = parseDate(a.reuniao1);
    const db = parseDate(b.reuniao1);
    return (da || 0) - (db || 0);
  });

  for (let i = 0; i < sorted.length; i++) {
    const atual = sorted[i];
    const reuniao1Atual = parseDate(atual.reuniao1);
    const anterior = i > 0 ? sorted[i - 1] : null;
    const reuniao1Anterior = anterior ? parseDate(anterior.reuniao1) : null;
    if (!reuniao1Atual) continue;

    const abreEm = anterior && reuniao1Anterior ? addDays(reuniao1Anterior, 1) : null;
    const fechaEm = addDays(reuniao1Atual, -1);

    const abertaApos = !anterior || !abreEm || hoje >= abreEm;
    const abertaAntes = hoje <= fechaEm;

    if (abertaApos && abertaAntes) return { status: STATUS.OPEN, turma: atual };
  }
  return { status: STATUS.CLOSED, turma: null };
};

const OPCOES = [
  { value: 'batismo',       label: 'Apenas Batismo',                  taxa: true  },
  { value: 'curso',         label: 'Apenas Curso de Pais e Padrinhos', taxa: false },
  { value: 'batismo_curso', label: 'Batismo + Curso',                  taxa: true  },
];

const initialForm = {
  opcao: '', nomeFilho: '', dataNascimento: '',
  nomePai: '', nomeMae: '', telefone: '',
  nomePadrinho: '', nomeMadrina: '',
};

const MultiFileUpload = ({ label, required, accept, error, onChange }) => {
  const ref = React.useRef();
  const [files, setFiles] = useState([]);
  const handleFiles = (newFiles) => {
    const arr = [...files, ...Array.from(newFiles)];
    setFiles(arr);
    if (onChange) onChange(arr);
  };
  const removeFile = (i) => {
    const arr = files.filter((_, idx) => idx !== i);
    setFiles(arr);
    if (onChange) onChange(arr);
  };
  return (
    <div className="form-group">
      <label className="form-label">{label} {required && <span className="required">*</span>}</label>
      <div className={`upload-area${error ? ' error' : ''}`}
        style={{ border: error ? '2px dashed var(--danger)' : undefined }}
        onClick={() => ref.current.click()}
        onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('dragging'); }}
        onDragLeave={e => e.currentTarget.classList.remove('dragging')}
        onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('dragging'); handleFiles(e.dataTransfer.files); }}>
        <input ref={ref} type="file" accept={accept} multiple onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
        <div className="upload-icon">📎</div>
        <p className="upload-label"><strong>Arraste os arquivos</strong> ou clique para selecionar</p>
        <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.25rem' }}>Múltiplos arquivos · {accept}</p>
      </div>
      {files.length > 0 && (
        <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {files.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--off-white)', borderRadius: 'var(--radius-sm)', padding: '0.5rem 0.75rem', fontSize: '0.85rem' }}>
              <span>📄 <strong>{f.name}</strong> <span style={{ color: 'var(--gray-400)' }}>({formatFileSize(f.size)})</span></span>
              <button type="button" onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '1rem' }}>✕</button>
            </div>
          ))}
        </div>
      )}
      {error && <p className="form-error">{error}</p>}
    </div>
  );
};

const Batismo = () => {
  const [turmas, setTurmas] = useState([]);
  const [loadingTurmas, setLoadingTurmas] = useState(true);
  const [inscricaoInfo, setInscricaoInfo] = useState({ status: STATUS.CLOSED, turma: null });
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState({});
  const [toast, setToast] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getTurmasBatismo()
      .then(r => {
        const t = r?.data || [];
        setTurmas(t);
        setInscricaoInfo(calcStatus(t));
      })
      .catch(() => {})
      .finally(() => setLoadingTurmas(false));
  }, []);

  const opcaoInfo = OPCOES.find(o => o.value === form.opcao);
  const temTaxa = opcaoInfo?.taxa ?? false;
  const idade = form.dataNascimento ? calcIdade(form.dataNascimento) : null;
  const idadeInvalida = idade !== null && idade > 6;

  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === 'telefone') v = maskPhone(value);
    setForm(f => ({ ...f, [name]: v }));
    setErrors(err => ({ ...err, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.opcao) errs.opcao = 'Selecione uma opção.';
    if (!form.nomeFilho.trim()) errs.nomeFilho = 'Nome obrigatório.';
    if (!form.dataNascimento) errs.dataNascimento = 'Data obrigatória.';
    if (idadeInvalida) errs.dataNascimento = 'Criança com mais de 6 anos. Procure a secretaria.';
    if (!form.nomeMae.trim() && !form.nomePai.trim()) errs.nomePai = 'Informe ao menos um responsável.';
    if (!form.telefone.trim()) errs.telefone = 'Telefone obrigatório.';
    if (['batismo', 'batismo_curso'].includes(form.opcao)) {
      if (!files.certidao) errs.certidao = 'Certidão obrigatória.';
      if (!files.docPadrinhos || files.docPadrinhos.length === 0) errs.docPadrinhos = 'Documento dos padrinhos obrigatório.';
    }
    if (['curso', 'batismo_curso'].includes(form.opcao)) {
      if (!files.docParticipantes || files.docParticipantes.length === 0) errs.docParticipantes = 'Documento dos participantes obrigatório.';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setLoading(true);
    try {
      const turmaNome = (inscricaoInfo.turma?.nome || 'Turma').replace(/\s+/g, '_');
      const pastaBase = `batismo/${turmaNome}`;

      const urlCertidao       = files.certidao
        ? await uploadArquivo(files.certidao, `${pastaBase}/certidoes`) : '';
      const urlsPadrinhos     = files.docPadrinhos?.length
        ? await uploadMultiplos(files.docPadrinhos, `${pastaBase}/doc_padrinhos`) : [];
      const urlsParticipantes = files.docParticipantes?.length
        ? await uploadMultiplos(files.docParticipantes, `${pastaBase}/doc_participantes`) : [];

      const dados = {
        turmaId:             inscricaoInfo.turma?.id || '',
        turmaNome:           inscricaoInfo.turma?.nome || '',
        ...form,
        urlCertidao,
        urlDocPadrinhos:     urlsPadrinhos.join('|'),
        urlDocParticipantes: urlsParticipantes.join('|'),
        urlComprovante:      '',
      };

      const res = await createInscricaoBatismo(dados);
      if (res?.success) {
        setSubmitted(true);
        setForm(initialForm);
        setFiles({});
      } else {
        setToast({ msg: res?.message || 'Erro ao enviar inscrição.', type: 'error' });
      }
    } catch {
      setToast({ msg: 'Erro de conexão. Tente novamente.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <main style={{ paddingTop: '72px' }}>
        <section style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-3xl) var(--space-xl)' }}>
          <div style={{ textAlign: 'center', maxWidth: 560 }}>
            <img src={batismoIcon} alt="Batismo" style={{ width: 100, display: 'block', margin: '0 auto 1.25rem', filter: 'invert(20%) sepia(50%) saturate(500%) hue-rotate(190deg)' }} />
            <h2 style={{ marginBottom: '1rem' }}>Inscrição Realizada!</h2>
            <div style={{ background: 'var(--gold-pale)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-lg) var(--space-xl)', marginBottom: '2rem', borderLeft: '4px solid var(--gold)' }}>
              <p style={{ color: 'var(--navy)', fontWeight: 600, marginBottom: '0.5rem' }}>✅ Sua inscrição já foi confirmada!</p>
              <p style={{ color: 'var(--gray-600)', fontSize: '0.95rem', lineHeight: 1.7 }}>
                Basta comparecer no <strong>primeiro dia da reunião</strong> da turma <strong>{inscricaoInfo.turma?.nome || 'selecionada'}</strong>,
                no <strong>Salão Paroquial da Igreja</strong>, às <strong>19h</strong>.
                O pagamento será realizado na primeira reunião.
              </p>
            </div>
            <button className="btn btn-primary" onClick={() => { setSubmitted(false); setInscricaoInfo(calcStatus(turmas)); }}>
              Nova Inscrição
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main style={{ paddingTop: '72px' }}>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, var(--navy-dark) 0%, var(--navy) 100%)', padding: 'var(--space-2xl) var(--space-xl)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(201,160,48,0.07) 1px, transparent 1px)', backgroundSize: '40px 40px', pointerEvents: 'none' }} />
        <div style={{ position: 'relative' }}>
          <div className="ornament" style={{ justifyContent: 'center', marginBottom: '0.75rem' }}>Sacramento</div>
          <img src={batismoIcon} alt="Batismo" style={{ width: 80, display: 'block', margin: '0 auto 0.5rem', filter: 'invert(1)', opacity: 0.9 }} />
          <h1 style={{ color: 'var(--white)', marginBottom: '0.5rem' }}>Batismo</h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', maxWidth: 560, margin: '0 auto', lineHeight: 1.8 }}>
            O primeiro sacramento da vida cristã. Inscreva seu filho e participe desse momento sagrado.
          </p>
        </div>
      </section>

      {/* Turmas */}
      <section style={{ background: 'var(--off-white)', padding: 'var(--space-xl) var(--space-xl)' }}>
        <div className="container">
          <h2 className="section-title" style={{ marginBottom: '1.5rem' }}>Turmas Disponíveis</h2>
          {loadingTurmas ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--space-md)' }}>
              {[1, 2].map(i => (
                <div key={i} style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-lg)', borderTop: '4px solid var(--gray-200)', boxShadow: 'var(--shadow-sm)' }}>
                  {[1,2,3].map(j => <div key={j} style={{ height: 14, background: 'var(--gray-100)', borderRadius: 4, marginBottom: '0.5rem', animation: 'pulse 1.5s ease-in-out infinite' }} />)}
                </div>
              ))}
              <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
            </div>
          ) : turmas.length === 0 ? (
            <div className="alert alert-info" style={{ maxWidth: 600, margin: '0 auto' }}>Nenhuma turma cadastrada no momento.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--space-md)' }}>
              {turmas.map((t, i) => (
                <div key={i} className="card" style={{ padding: 'var(--space-lg)', borderTop: '4px solid var(--gold)' }}>
                  <h3 style={{ color: 'var(--navy)', marginBottom: '0.75rem', fontSize: '1.1rem' }}>{t.nome}</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {/* Reuniões */}
                    {[t.reuniao1, t.reuniao2, t.reuniao3, t.reuniao4].filter(Boolean).length > 0 && (
                      <>
                        <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.1rem' }}>Formações</p>
                        {[t.reuniao1, t.reuniao2, t.reuniao3, t.reuniao4].filter(Boolean).map((r, idx) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                            <span style={{ color: 'var(--gray-400)' }}>{idx + 1}ª Formação</span>
                            <strong>{formatDate(r)}</strong>
                          </div>
                        ))}
                        <div style={{ height: 1, background: 'var(--gray-200)', margin: '0.25rem 0' }} />
                      </>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                      <span style={{ fontWeight: 600 }}>Batismo</span>
                      <strong style={{ color: 'var(--gold)' }}>{formatDate(t.dataBatismo)}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Formulário */}
      <section style={{ background: 'var(--white)', padding: 'var(--space-xl) var(--space-xl)' }}>
        <div className="container" style={{ maxWidth: 900 }}>
          {!loadingTurmas && (inscricaoInfo.status !== STATUS.OPEN ? (
            <div style={{ background: 'linear-gradient(135deg, var(--navy) 0%, var(--navy-light) 100%)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-2xl)', textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⏳</div>
              <h3 style={{ color: 'var(--white)', marginBottom: '0.75rem' }}>Inscrições encerradas no momento</h3>
              <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.8 }}>
                As inscrições abrem no dia seguinte à primeira reunião da turma atual e fecham um dia antes da primeira reunião da próxima turma.
              </p>
            </div>
          ) : (
            <>
              <div style={{ background: 'linear-gradient(135deg, var(--success) 0%, #27ae60 100%)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-md) var(--space-xl)', marginBottom: 'var(--space-xl)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ fontSize: '1.5rem' }}>✅</span>
                <div>
                  <p style={{ color: 'var(--white)', fontWeight: 600, margin: 0 }}>Inscrições abertas — Turma {inscricaoInfo.turma?.nome}</p>
                  <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.875rem', margin: 0 }}>
                    Primeira reunião em {formatDate(inscricaoInfo.turma?.reuniao1)}
                  </p>
                </div>
              </div>

              <div className="form-card-mobile" style={{ background: 'var(--white)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', padding: 'var(--space-2xl)' }}>
                <h2 style={{ marginBottom: '0.25rem' }}>Formulário de Inscrição</h2>
                <p style={{ marginBottom: 'var(--space-lg)' }}>Preencha os dados para realizar a inscrição.</p>

                <form onSubmit={handleSubmit} noValidate>
                  {/* Tipo */}
                  <h4 style={{ color: 'var(--navy)', marginBottom: 'var(--space-md)', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gold-pale)' }}>Tipo de Inscrição</h4>
                  {errors.opcao && <p className="form-error" style={{ marginBottom: '0.5rem' }}>{errors.opcao}</p>}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: 'var(--space-lg)' }}>
                    {OPCOES.map(({ value, label, taxa }) => (
                      <label key={value} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', padding: '0.875rem 1rem', borderRadius: 'var(--radius-md)', border: `2px solid ${form.opcao === value ? 'var(--gold)' : 'var(--gray-200)'}`, background: form.opcao === value ? 'var(--gold-pale)' : 'var(--white)', transition: 'all 0.15s' }}>
                        <input type="radio" name="opcao" value={value} checked={form.opcao === value} onChange={handleChange} style={{ marginTop: '2px' }} />
                        <div>
                          <div style={{ fontWeight: form.opcao === value ? 600 : 400, fontSize: '0.9rem' }}>{label}</div>
                          <div style={{ fontSize: '0.75rem', color: taxa ? 'var(--warning)' : 'var(--success)', marginTop: '0.2rem' }}>
                            {taxa ? 'Taxa: R$ 100,00 (paga na 1ª reunião)' : 'Sem taxa'}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>

                  {temTaxa && (
                    <div className="alert alert-info" style={{ marginBottom: 'var(--space-lg)' }}>
                      💡 O pagamento da taxa de <strong>R$ 100,00</strong> será realizado presencialmente na <strong>primeira reunião</strong>, no Salão Paroquial às 19h.
                    </div>
                  )}

                  {/* Dados */}
                  <h4 style={{ color: 'var(--navy)', marginBottom: 'var(--space-md)', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gold-pale)' }}>Dados da Criança e Contato</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Nome Completo <span className="required">*</span></label>
                      <input name="nomeFilho" value={form.nomeFilho} onChange={handleChange} className={`form-input${errors.nomeFilho ? ' error' : ''}`} />
                      {errors.nomeFilho && <p className="form-error">{errors.nomeFilho}</p>}
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Data de Nascimento <span className="required">*</span></label>
                      <input type="date" name="dataNascimento" value={form.dataNascimento} onChange={handleChange}
                        className={`form-input${errors.dataNascimento ? ' error' : ''}`}
                        max={new Date().toISOString().split('T')[0]} />
                      {idade !== null && !idadeInvalida && <p style={{ fontSize: '0.8rem', color: 'var(--success)', marginTop: '0.25rem' }}>✓ Idade: {idade} {idade === 1 ? 'ano' : 'anos'}</p>}
                      {idadeInvalida && <div className="alert alert-danger" style={{ marginTop: '0.5rem', padding: '0.6rem' }}>Acima de 6 anos: procure a secretaria.</div>}
                      {errors.dataNascimento && <p className="form-error">{errors.dataNascimento}</p>}
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Telefone / WhatsApp <span className="required">*</span></label>
                      <input name="telefone" value={form.telefone} onChange={handleChange} placeholder="(85) 99000-0000"
                        className={`form-input${errors.telefone ? ' error' : ''}`} />
                      {errors.telefone && <p className="form-error">{errors.telefone}</p>}
                    </div>
                  </div>

                  <h4 style={{ color: 'var(--navy)', marginBottom: 'var(--space-md)', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gold-pale)' }}>Responsáveis</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Nome do Pai</label>
                      <input name="nomePai" value={form.nomePai} onChange={handleChange} className={`form-input${errors.nomePai ? ' error' : ''}`} />
                      {errors.nomePai && <p className="form-error">{errors.nomePai}</p>}
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Nome da Mãe</label>
                      <input name="nomeMae" value={form.nomeMae} onChange={handleChange} className="form-input" />
                    </div>
                    {['batismo', 'batismo_curso'].includes(form.opcao) && <>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Nome do Padrinho</label>
                        <input name="nomePadrinho" value={form.nomePadrinho} onChange={handleChange} className="form-input" />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Nome da Madrinha</label>
                        <input name="nomeMadrina" value={form.nomeMadrina} onChange={handleChange} className="form-input" />
                      </div>
                    </>}
                  </div>

                  <h4 style={{ color: 'var(--navy)', marginBottom: 'var(--space-md)', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gold-pale)' }}>Documentos</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'var(--space-md)' }}>
                    {['batismo', 'batismo_curso'].includes(form.opcao) && <>
                      <FileUpload label="Certidão de Nascimento" required accept=".pdf,.jpg,.jpeg,.png"
                        error={errors.certidao} onChange={(f) => setFiles(v => ({ ...v, certidao: f }))} />
                      <MultiFileUpload label="Documentos dos Padrinhos" required accept=".pdf,.jpg,.jpeg,.png"
                        error={errors.docPadrinhos} onChange={(arr) => setFiles(v => ({ ...v, docPadrinhos: arr }))} />
                    </>}
                    {['curso', 'batismo_curso'].includes(form.opcao) &&
                      <MultiFileUpload label="Documentos dos Participantes" required accept=".pdf,.jpg,.jpeg,.png"
                        error={errors.docParticipantes} onChange={(arr) => setFiles(v => ({ ...v, docParticipantes: arr }))} />
                    }
                  </div>

                  <div className="submit-row" style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 'var(--space-lg)', borderTop: '1px solid var(--gray-200)', marginTop: 'var(--space-lg)' }}>
                    <button type="submit" className="btn btn-primary btn-lg" disabled={loading || idadeInvalida}>
                      {loading ? 'Enviando...' : '✦ Enviar Inscrição'}
                    </button>
                  </div>
                </form>
              </div>
            </>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Batismo;